import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, CalendarClock, ArrowRight, Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import MobileScreenHeader from "../components/MobileScreenHeader";

export default function MySubscriptions() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [cancelling, setCancelling] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/subscriptions/my-plan");
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const cancel = async () => {
        if (!window.confirm("Cancel your active plan? You'll keep benefits until expiry.")) return;
        setCancelling(true);
        try {
            await api.post("/subscriptions/cancel");
            toast.success("Subscription cancelled");
            load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setCancelling(false);
        }
    };

    const activePlan  = data?.active_plan;
    const isPaidActive = activePlan && activePlan !== "trial" && activePlan !== "free" && data?.subscription_status !== "cancelled";
    const history     = data?.history || [];
    const expiresAt   = data?.plan_expires_at;

    return (
        <div className="min-h-screen bg-[#f6f7fb]" data-testid="my-subscriptions-page">
            <MobileScreenHeader title="My Subscriptions" subtitle="Active plan & history" testid="subs-header" />

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#002657]/60">
                    <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
                </div>
            ) : (
                <>
                    {/* Active plan card */}
                    <div className="px-4 -mt-2">
                        <div
                            className="text-on-image rounded-2xl p-5 shadow-xl"
                            style={{ background: "linear-gradient(135deg, #001a3d 0%, #002657 60%, #003366 100%)", boxShadow: "0 15px 35px -12px rgba(0,15,35,0.55)" }}
                            data-testid="subs-active-card"
                        >
                            <div className="flex items-center justify-between">
                                <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: "#F5E5A5" }}>Current Plan</div>
                                <Crown className="w-5 h-5" style={{ color: "#F5E5A5" }} />
                            </div>
                            <div className="mt-2 font-heading font-extrabold text-white text-2xl capitalize" data-testid="subs-active-plan">
                                {activePlan || "Free"}
                            </div>
                            {expiresAt && (
                                <div className="mt-1 text-[11px] text-white/75 inline-flex items-center gap-1">
                                    <CalendarClock className="w-3 h-3" /> Expires on {expiresAt.slice(0, 10)}
                                </div>
                            )}
                            <div className="mt-4 flex items-center gap-2">
                                {isPaidActive ? (
                                    <button
                                        type="button"
                                        onClick={cancel}
                                        disabled={cancelling}
                                        data-testid="subs-cancel-btn"
                                        className="flex-1 px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-[12px] font-bold disabled:opacity-60"
                                    >
                                        {cancelling ? "…" : "Cancel Plan"}
                                    </button>
                                ) : null}
                                <Link
                                    to="/pricing"
                                    data-testid="subs-upgrade-btn"
                                    className="flex-1 px-3 py-2 rounded-lg text-[12px] font-extrabold text-center shadow"
                                    style={{ background: "linear-gradient(90deg, #DAA520, #F59E0B)", color: "#001a3d" }}
                                >
                                    {isPaidActive ? "Upgrade" : "See Plans"} <ArrowRight className="w-3 h-3 inline" />
                                </Link>
                            </div>
                        </div>
                    </div>

                    {/* History */}
                    <div className="mt-5 px-4 pb-6">
                        <div className="text-[11px] uppercase tracking-widest font-extrabold text-[#8B6712] mb-2">Subscription History</div>
                        {history.length === 0 ? (
                            <div className="rounded-2xl bg-white border border-[#002657]/10 p-6 text-center text-[#002657]/60 text-sm" data-testid="subs-history-empty">
                                No past subscriptions.
                            </div>
                        ) : (
                            <div className="space-y-2" data-testid="subs-history-list">
                                {history.map((h) => {
                                    const ok = h.status === "active" || h.status === "captured" || h.status === "success";
                                    return (
                                        <div key={h.id} className="rounded-2xl bg-white border border-[#002657]/10 p-3 flex items-center gap-3" data-testid={`subs-item-${h.id}`}>
                                            <div className={`w-9 h-9 rounded-full flex items-center justify-center ${ok ? "bg-emerald-50 text-emerald-600" : "bg-red-50 text-red-600"}`}>
                                                {ok ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[13px] font-bold text-[#002657] truncate capitalize">{h.plan_id || h.plan || "Plan"}</div>
                                                <div className="text-[10px] text-[#002657]/55">{(h.created_at || "").slice(0, 10)} · {h.status}</div>
                                            </div>
                                            <div className="text-[13px] font-extrabold text-[#8B6712]">₹{(h.amount_inr ?? h.payable_inr ?? h.amount ?? 0).toLocaleString("en-IN")}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}
